import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import OrderSummarySection from './components/OrderSummarySection';
import DeliveryAddressSection from './components/DeliveryAddressSection';
import OrderTrackingSection from './components/OrderTrackingSection';
import EstimatedArrivalSection from './components/EstimatedArrivalSection';

const OrderConfirmation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [orderData, setOrderData] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Mock order data - in production this would come from state/API
    const mockOrderData = {
      orderId: `ORD-${Date.now()}`,
      orderDate: new Date()?.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),
      status: 'confirmed',
      items: [
        {
          id: 1,
          name: "Classic Chapati Combo",
          quantity: 2,
          price: 1500,
          customization: "Extra spicy"
        },
        {
          id: 2,
          name: "Spicy Beef Chapati Wrap",
          quantity: 1,
          price: 1200,
          customization: null
        }
      ],
      deliveryAddress: {
        zone: "Juba City Center",
        street: "Juba-Nimule Road, Near Konyo Konyo Market",
        landmark: "Opposite Blue Nile Hotel",
        phone: "+211 915 497 014"
      },
      deliveryFee: 500,
      subtotal: 4200,
      total: 4700,
      estimatedTime: "20-30 min",
      preparationTime: 15,
      deliveryTime: 20
    };

    setOrderData(mockOrderData);
  }, []);

  const handleWhatsAppContact = () => {
    const message = `Hello! I have a question about my order ${orderData?.orderId}.`;
    window.open(`https://wa.me/211915497014?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleCallRestaurant = () => {
    window.open('tel:+211915497014', '_self');
  };

  if (!orderData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} color="var(--color-primary)" className="animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading order details...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Order Confirmation - Chapati Plus | Your Order is Confirmed</title>
        <meta name="description" content="Your Chapati Plus order has been confirmed. Track your order status and estimated delivery time." />
      </Helmet>
      <div className="min-h-screen bg-background flex flex-col">
        <Header />

        <main className="flex-1 pt-20 pb-8 md:pb-12 lg:pb-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            {/* Success Header */}
            <div className="text-center mb-6 md:mb-8 lg:mb-12">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
                <Icon name="CheckCircle2" size={40} color="var(--color-accent)" />
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-3 md:mb-4">
                Order Confirmed!
              </h1>
              <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto mb-2">
                Thank you for your order. We're preparing your delicious meal!
              </p>
              <p className="text-sm md:text-base text-muted-foreground">
                Order ID: <span className="font-semibold text-foreground">{orderData?.orderId}</span>
              </p>
              <p className="text-xs md:text-sm text-muted-foreground mt-1">
                {orderData?.orderDate}
              </p>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
              {/* Left Column - Order Details */}
              <div className="lg:col-span-2 space-y-4 md:space-y-6">
                <OrderTrackingSection status={orderData?.status} orderId={orderData?.orderId} />
                <EstimatedArrivalSection 
                  estimatedTime={orderData?.estimatedTime}
                  preparationTime={orderData?.preparationTime}
                  deliveryTime={orderData?.deliveryTime}
                />
                <DeliveryAddressSection address={orderData?.deliveryAddress} />
                <OrderSummarySection 
                  items={orderData?.items}
                  subtotal={orderData?.subtotal}
                  deliveryFee={orderData?.deliveryFee}
                  total={orderData?.total}
                />
              </div>

              {/* Right Column - Actions & Support */}
              <div className="space-y-4 md:space-y-6">
                {/* Quick Actions Card */}
                <div className="bg-card rounded-xl shadow-lg p-4 md:p-6 sticky top-20">
                  <h2 className="text-lg md:text-xl font-bold text-foreground mb-4 md:mb-6">Quick Actions</h2>
                  <div className="space-y-3">
                    <Button
                      variant="default"
                      size="lg"
                      fullWidth
                      iconName="MessageCircle"
                      iconPosition="left"
                      onClick={handleWhatsAppContact}
                      className="bg-accent hover:bg-accent/90"
                    >
                      Contact via WhatsApp
                    </Button>
                    <Button
                      variant="outline"
                      size="lg"
                      fullWidth
                      iconName="Phone"
                      iconPosition="left"
                      onClick={handleCallRestaurant}
                    >
                      Call Restaurant
                    </Button>
                    <Button
                      variant="ghost"
                      size="lg"
                      fullWidth
                      iconName="Home"
                      iconPosition="left"
                      onClick={() => navigate('/homepage')}
                    >
                      Back to Home
                    </Button>
                  </div>
                </div>

                {/* Support Info Card */}
                <div className="bg-primary/10 rounded-xl border border-primary/30 p-4 md:p-6">
                  <div className="flex gap-3 mb-3">
                    <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-foreground text-sm md:text-base mb-2">Need Help?</h3>
                      <p className="text-xs md:text-sm text-muted-foreground mb-3">
                        Our team is here to assist you with any questions or concerns about your order.
                      </p>
                      <div className="space-y-2 text-xs md:text-sm">
                        <div className="flex items-center gap-2 text-foreground">
                          <Icon name="Phone" size={14} />
                          <span>+211 915 497 014</span>
                        </div>
                        <div className="flex items-center gap-2 text-foreground">
                          <Icon name="Clock" size={14} />
                          <span>Available 24/7</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Delivery Info */}
                <div className="bg-accent/10 rounded-xl border border-accent/30 p-4 md:p-6">
                  <div className="flex gap-3">
                    <Icon name="Truck" size={20} color="var(--color-accent)" className="flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-foreground text-sm md:text-base mb-2">Delivery Notes</h3>
                      <ul className="text-xs md:text-sm text-muted-foreground space-y-1">
                        <li>• Free delivery on orders above SSP 5,000</li>
                        <li>• Times may vary based on traffic</li>
                        <li>• Driver will call before arrival</li>
                        <li>• Contactless delivery available</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default OrderConfirmation;